import React, { useState, useEffect } from 'react';
import { HeadphonesIcon, CheckCircle, Monitor, BookOpen } from 'lucide-react';
import api from '../api';

const APP_ERROR_OPTIONS = [
  { value: 'CL', label: 'No puedo loguearme' },
  { value: 'NR', label: 'No me llega el correo de recuperación de contraseña' },
  { value: 'NS', label: 'Puedo entrar pero no me figuran las materias' },
  { value: 'NA', label: 'Veo la materia pero no veo el botón para dar el presente' },
  { value: 'AI', label: 'Doy el presente pero no veo que impacte en la aplicación' },
  { value: 'NP', label: 'No veo las asistencias asentadas en clases anteriores' },
  { value: 'OT', label: 'Otro' },
];

const CAMPUS_ERROR_OPTIONS = [
  { value: 'CM', label: 'No me llegó el correo de acceso al campus' },
  { value: 'CC', label: 'No puedo ingresar al campus' },
  { value: 'CF', label: 'No encuentro el material del campus' },
  { value: 'OT', label: 'Otro' },
];

const SupportModal = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [systemType, setSystemType] = useState('');
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [dni, setDni] = useState('');
  const [email, setEmail] = useState('');
  const [selectedDiplo, setSelectedDiplo] = useState('');
  const [errorType, setErrorType] = useState('');
  const [otherDesc, setOtherDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [diplomas, setDiplomas] = useState([]);

  useEffect(() => {
    api.get('support/active_diplomas/')
      .then(res => setDiplomas(res.data))
      .catch(() => {});
  }, []);

  const errorOptions = systemType === 'CA' ? CAMPUS_ERROR_OPTIONS : APP_ERROR_OPTIONS;

  const handleSystemSelect = (type) => {
    setSystemType(type);
    setErrorType('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre.trim() || !apellido.trim() || !dni.trim()) { setError('Completá nombre, apellido y DNI.'); return; }
    if (!email || !errorType) { setError('Completá el correo y seleccioná un problema.'); return; }
    if (errorType === 'OT' && !otherDesc.trim()) { setError('Describí el problema en el campo de observaciones.'); return; }
    setError('');
    setLoading(true);

    const payload = {
      system_type: systemType,
      nombre,
      apellido,
      dni,
      email,
      error_type: errorType,
      other_description: otherDesc,
      ...(selectedDiplo ? { diploma: parseInt(selectedDiplo) } : {}),
    };

    try {
      await api.post('support/', payload);
      setStep(2);
    } catch {
      setError('No se pudo enviar el reporte. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 11000 }}>
      <div
        className="modal-content glassmorphism animate-fade-in"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '100vw', width: '100%', maxHeight: '100vh', height: '100vh', overflowY: 'auto', borderRadius: 0 }}
      >
        {step === 2 ? (
          <div className="text-center py-5 px-4">
            <CheckCircle size={56} className="text-success mb-3" />
            <h4 className="fw-bold mb-2">¡Reporte enviado!</h4>
            <p className="text-muted mb-4">
              Tu solicitud fue registrada. El equipo técnico la revisará a la brevedad.
            </p>
            <button className="btn btn-primary px-5" onClick={onClose}>Cerrar</button>
          </div>
        ) : (
          <>
            <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
              <h5 className="modal-title section-title fs-5 mb-0 d-flex align-items-center gap-2">
                <HeadphonesIcon size={22} className="text-warning" /> Soporte Técnico
              </h5>
              <button className="btn-close" onClick={onClose} />
            </div>

            <div className="p-4 pt-3">
              {/* System type selector */}
              <div className="mb-4">
                <label className="form-label fw-semibold mb-2">¿Sobre qué sistema es tu consulta?</label>
                <div className="row g-2">
                  {[
                    { value: 'AP', label: 'App de Asistencias', icon: <Monitor size={28} />, desc: 'Login, materias, asistencia' },
                    { value: 'CA', label: 'Campus Virtual', icon: <BookOpen size={28} />, desc: 'Correo, acceso, materiales' },
                  ].map(opt => (
                    <div className="col-6" key={opt.value}>
                      <button
                        type="button"
                        onClick={() => handleSystemSelect(opt.value)}
                        className={`w-100 btn d-flex flex-column align-items-center py-3 gap-1 border-2 ${
                          systemType === opt.value
                            ? 'btn-warning border-warning'
                            : 'btn-outline-secondary border-light'
                        }`}
                        style={{ borderRadius: 12, minHeight: 90 }}
                      >
                        {opt.icon}
                        <span className="fw-semibold small">{opt.label}</span>
                        <span className="text-muted" style={{ fontSize: 11 }}>{opt.desc}</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rest of the form — visible only after system type is selected */}
              {systemType && (
                <form onSubmit={handleSubmit}>
                  {/* Personal data */}
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <label className="form-label fw-semibold">Nombre</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Tu nombre"
                        value={nombre}
                        onChange={e => setNombre(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-semibold">Apellido</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Tu apellido"
                        value={apellido}
                        onChange={e => setApellido(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold">DNI</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Sin puntos ni espacios"
                        value={dni}
                        onChange={e => setDni(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {systemType === 'CA'
                        ? 'Correo con el que intentás acceder al campus'
                        : 'Correo con el que intentás ingresar a la app'}
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="tu@correo.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  {/* Diplomatura */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">¿Qué estás cursando?</label>
                    <select
                      className="form-select"
                      value={selectedDiplo}
                      onChange={e => setSelectedDiplo(e.target.value)}
                    >
                      <option value="">Seleccioná tu diplomatura...</option>
                      {diplomas.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.name}{d.municipio ? ` – ${d.municipio}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Error type */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">¿Cuál es el problema?</label>
                    <div className="d-flex flex-column gap-2">
                      {errorOptions.map(opt => (
                        <label
                          key={opt.value}
                          className={`d-flex align-items-start gap-2 p-2 rounded border cursor-pointer ${errorType === opt.value ? 'border-primary bg-primary bg-opacity-10' : 'border-light'}`}
                          style={{ cursor: 'pointer' }}
                        >
                          <input
                            type="radio"
                            name="errorType"
                            value={opt.value}
                            checked={errorType === opt.value}
                            onChange={() => setErrorType(opt.value)}
                            className="mt-1 flex-shrink-0"
                          />
                          <span className="small">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Observaciones — always visible, required for "Otro" */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {errorType === 'OT' ? 'Describí el problema *' : 'Observaciones adicionales'}
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Contanos qué está pasando..."
                      value={otherDesc}
                      onChange={e => setOtherDesc(e.target.value)}
                    />
                  </div>

                  {error && <div className="alert alert-danger py-2 small">{error}</div>}

                  <button
                    type="submit"
                    className="btn btn-warning w-100 fw-bold"
                    disabled={loading}
                  >
                    {loading ? 'Enviando...' : 'Enviar Reporte'}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SupportModal;
